import { useCallback, useMemo } from 'react';
import { createHttpClient } from '@adapters/HttpClient';
import { InvoicesApiAdapter } from '@modules/Invoices/Invoices/List/adapters/InvoicesApiAdapter';
import type { Invoice } from '@modules/Invoices/Invoices/List/models/Invoices';
import type { DynamicComboboxOption } from '@shared/components/ui/DynamicCombobox';
import { apiClientWH } from '@shared/services/ApiClient';

function formatInvoiceLabel(invoice: Pick<Invoice, 'id' | 'erp_number' | 'preinvoice'>): string {
  return invoice.erp_number || invoice.preinvoice?.code || `INV-${invoice.id}`;
}

export function useInvoiceAuditInvoiceOptions() {
  const adapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new InvoicesApiAdapter(httpClient);
  }, []);

  const searchInvoices = useCallback(
    async (query: string): Promise<DynamicComboboxOption[]> => {
      const result = await adapter.getInvoicesPaginated({
        search: query.trim() || undefined,
        page: 1,
        limit: 50,
        sortBy: 'id',
        sortDirection: 'desc',
      });

      return result.invoices.map((invoice) => ({
        label: formatInvoiceLabel(invoice),
        value: String(invoice.id),
      }));
    },
    [adapter],
  );

  const resolveInvoiceOption = useCallback(async (value: string): Promise<DynamicComboboxOption | null> => {
    if (!value.trim()) return null;

    try {
      const httpClient = createHttpClient(apiClientWH);
      const invoice = await httpClient.get<Invoice>(`/invoices/${value}`, {}, {}, false);

      return {
        label: formatInvoiceLabel(invoice),
        value: String(invoice.id),
      };
    } catch {
      return null;
    }
  }, []);

  return { searchInvoices, resolveInvoiceOption };
}
